import { BarChart2, Home, LogOut, Menu, Package, Settings, ShoppingCart, Store, Tag, Truck, Users, X, Layers, Link as LinkIcon, Bell, TicketPercent, Ship, CreditCard, Send, FileSpreadsheet, Printer, Download, Puzzle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FaWhatsapp } from "react-icons/fa";
import useStoreConfig from '../hooks/useStoreConfig';
import { AuthService } from '../services/authService';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsEvents } from '../utils/analytics';
import { Banner } from '../types/banner';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { usePWAInstall } from '../hooks/usePWAInstall';
import AppInstallBanner from '../components/AppInstallBanner';



// Add interface for store data
interface StoreData {
  brandName: string;
  storeUrl: string;
  logoUrl: string | null;
  banners: Banner[];
  socialLinks: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
}

interface MenuItem {
  name: string;
  icon: any;
  path: string;
  // Soft, light icon-badge tint: "bg-{c}-50 text-{c}-600". The text colour also
  // drives the (currentColor) Lucide icon, so the whole badge stays on-palette.
  color: string;
  subItems?: {
    name: string;
    path: string;
  }[];
}

const AdminHeader = () => {
  const WHATSAPP_NUMBER = "917518388162";
  const WHATSAPP_MESSAGE = "Hi, I need support.";
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('Home');
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading } = useStoreConfig();
  const storeData = data as StoreData;
  const { track, reset } = useAnalytics();
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
  const isDashboardRoute = location.pathname === "/dashboard";
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const { isSubscribed } = usePushNotifications();
  const {
    isInstalled,
    installApp,
    deferredPrompt,
    showPrompt: showInstallBanner,
    dismissPrompt: dismissInstallBanner,
  } = usePWAInstall();

  const notificationsEnabled = isSubscribed;
  const isOnNotificationsPage = location.pathname === '/notifications';
  const NOTIF_BANNER_DISMISS_KEY = 'notif_banner_last_dismissed';
  const [showNotifBanner, setShowNotifBanner] = useState(() => {
    const lastDismissed = localStorage.getItem(NOTIF_BANNER_DISMISS_KEY);
    if (!lastDismissed) return true;
    // Re-show the banner 2 days after it was last dismissed
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    return new Date(lastDismissed) < twoDaysAgo;
  });

  const dismissNotifBanner = () => {
    setShowNotifBanner(false);
    localStorage.setItem(NOTIF_BANNER_DISMISS_KEY, new Date().toISOString());
  };

  const handleInstallClick = async () => {
    const installed = await installApp();
    if (!installed) {
      // No native install prompt available (in-app browser or iOS) — show manual steps
      setShowInstallDialog(true);
    }
  };

  const handleNotificationClick = () => {
    navigate('/notifications');
  };


  useEffect(() => {
    if (!isLoading && data === null) {
      navigate('/onboarding');
    }
  }, [data, isLoading]);


  const headerTitle = isLoading ? (
    <div className="animate-pulse">
      <div className="h-6 w-32 bg-gray-200 rounded"></div>
    </div>
  ) : (
    activeItem === 'Home' ? storeData?.brandName : activeItem
  );

  useEffect(() => {
    const segment = location.pathname === '/dashboard' ? '' : (location.pathname.split('/')[1] || '');
    const foundItem = menuItems.find((item) => item.path === segment);
    setActiveItem(foundItem ? foundItem.name : 'Home');
  }, [location.pathname]);

  // Check screen size on mount and when window resizes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const menuItems: MenuItem[] = [
    { name: 'Home', icon: Home, path: '', color: 'bg-indigo-50 text-indigo-600' },
    { name: 'Orders', icon: ShoppingCart, path: 'orders', color: 'bg-sky-50 text-sky-600' },
    { name: 'Products', icon: Package, path: 'products', color: 'bg-violet-50 text-violet-600' },
    { name: 'Collections', icon: Layers, path: 'collections', color: 'bg-fuchsia-50 text-fuchsia-600' },
    { name: 'Categories', icon: Tag, path: 'categories', color: 'bg-amber-50 text-amber-600' },
    { name: 'Coupons', icon: TicketPercent, path: 'coupons', color: 'bg-rose-50 text-rose-600' },
    { name: 'Create Link', icon: LinkIcon, path: 'create-link', color: 'bg-cyan-50 text-cyan-600' },
    { name: 'Auto DM', icon: Send, path: 'auto-dm', color: 'bg-teal-50 text-teal-600' },
    { name: 'Customers', icon: Users, path: 'customers', color: 'bg-emerald-50 text-emerald-600' },
    { name: 'Payments', icon: BarChart2, path: 'payments', color: 'bg-green-50 text-green-600' },
    { name: 'Billing', icon: CreditCard, path: 'billing', color: 'bg-orange-50 text-orange-600' },
    { name: 'Reports', icon: FileSpreadsheet, path: 'reports', color: 'bg-blue-50 text-blue-600' },
    { name: 'Delivery Charges', icon: Truck, path: 'delivery-charges', color: 'bg-purple-50 text-purple-600' },
    { name: 'Shipping Partners', icon: Ship, path: 'shipping-partners', color: 'bg-teal-50 text-teal-600' },
    { name: 'Label Download', icon: Printer, path: 'label-download', color: 'bg-pink-50 text-pink-600' },
    { name: 'Store', icon: Store, path: 'store', color: 'bg-indigo-50 text-indigo-600' },
    { name: 'Integrations', icon: Puzzle, path: 'integrations', color: 'bg-violet-50 text-violet-600' },
    { name: 'Notifications', icon: Bell, path: 'notifications', color: 'bg-amber-50 text-amber-600' },
    { name: 'Settings', icon: Settings, path: 'settings', color: 'bg-slate-100 text-slate-600' },
  ];

  // Bottom navigation items (subset of main menu)
  const bottomNavItems = [
    { name: 'Home', icon: Home, path: '', color: 'bg-indigo-50 text-indigo-600' },
    { name: 'Orders', icon: ShoppingCart, path: 'orders', color: 'bg-sky-50 text-sky-600' },
    { name: 'Products', icon: Package, path: 'products', color: 'bg-violet-50 text-violet-600' },
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleMenuClick = (itemName: string, path: string) => {
    setActiveItem(itemName);
    navigate(path === '' ? '/dashboard' : `/${path}`);
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogout = async () => {
    // Track logout event
    track({
      name: AnalyticsEvents.USER_LOGOUT,
      properties: {
        logout_method: 'manual',
        page: location.pathname,
      },
    });

    // Reset analytics user
    reset();

    await AuthService.logout();
    navigate("/")
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Top Navigation Bar - Only visible on small/medium screens */}
      {!isLargeScreen && (
        <header className="bg-white shadow-sm lg:hidden relative z-50">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center">
              <button
                onClick={toggleSidebar}
                className="p-1 rounded-md text-gray-500 hover:text-gray-900 focus:outline-none"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="ml-3 font-bold text-lg text-gray-900 truncate max-w-[150px] sm:max-w-[200px]">
                {storeData?.brandName || "Trendy"}
              </div>
            </div>
            <div className="flex items-center gap-3.5">
              {!isInstalled && (
                <button
                  onClick={handleInstallClick}
                  className="text-gray-500 hover:text-indigo-600 transition-colors"
                  title="Install App"
                >
                  <Download className="w-5 h-5" />
                </button>
              )}
              {!isSubscribed && (
                <button
                  onClick={handleNotificationClick}
                  className="relative text-[#F59E0B] hover:text-[#D97706] transition-colors"
                  title="Enable Notifications"
                >
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-[#F97316] ring-2 ring-white" />
                </button>
              )}
              {isDashboardRoute && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#22C55E] flex items-center justify-center text-white shadow-sm hover:bg-[#16A34A] transition-colors"
                  aria-label="Contact WhatsApp support"
                >
                  <FaWhatsapp className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Install App Dialog */}
      {showInstallDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-5 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Download className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold">Install the App</h3>
              <p className="text-white/80 text-sm mt-1">Add to your home screen for the best experience</p>
            </div>

            {/* Steps */}
            <div className="p-5 space-y-4">
              <p className="text-gray-600 text-sm text-center">
                To install this app, please open this page in <strong>Chrome</strong> or <strong>Safari</strong> browser:
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-700 font-bold text-sm">1</div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Copy the page URL</p>
                    <p className="text-xs text-gray-500 mt-0.5">Tap the address bar and copy the full link</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-700 font-bold text-sm">2</div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Open Chrome or Safari</p>
                    <p className="text-xs text-gray-500 mt-0.5">Paste the URL and navigate to the page</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-700 font-bold text-sm">3</div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Install from the browser</p>
                    <p className="text-xs text-gray-500 mt-0.5">Use "Add to Home Screen" when prompted</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowInstallDialog(false)}
                className="w-full mt-2 py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Always visible on large screens, toggleable on small/medium */}
        <aside
          className={`bg-white text-gray-800 shadow-[1px_0_3px_rgba(16,24,40,0.04)] w-64 ${isLargeScreen ? 'relative' : 'fixed inset-y-0 left-0 transform'
            } ${(isLargeScreen || isSidebarOpen) ? 'translate-x-0' : '-translate-x-full'
            } transition-transform duration-300 ease-in-out z-50 h-full flex flex-col`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between gap-2 p-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-lg text-gray-900 truncate">{storeData?.brandName}</div>
            </div>
            {!isLargeScreen && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Sidebar Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeItem === item.name;
                const hasSubItems = 'subItems' in item && item.subItems !== undefined;

                return (
                  <li key={item.name}>
                    <button
                      onClick={() => handleMenuClick(item.name, item.path)}
                      className={`group flex items-center gap-3 w-full text-left px-2.5 py-2 rounded-lg transition-colors ${isActive
                        ? 'bg-indigo-50 text-indigo-700 font-semibold'
                        : 'text-gray-700 font-medium hover:bg-gray-50'
                        }`}
                    >
                      <span className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${item.color}`}>
                        <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                      </span>
                      <span className="text-sm truncate">{item.name}</span>
                    </button>
                    {hasSubItems && isActive && item.subItems && (
                      <ul className="mt-1 ml-11 space-y-1">
                        {item.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <button
                              onClick={() => handleMenuClick(subItem.name, subItem.path)}
                              className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm rounded-md text-gray-600 hover:bg-gray-50 hover:text-indigo-600"
                            >
                              <span>{subItem.name}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout Button - Fixed at Bottom */}
          <div className="p-3 border-t border-gray-100 flex-shrink-0">
            <button onClick={handleLogout} className="group flex items-center gap-3 px-2.5 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-50 w-full text-left transition-colors">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 bg-rose-50 text-rose-600">
                <LogOut className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 overflow-y-auto bg-gray-50 ${!isLargeScreen ? 'pb-16' : 'pb-0'}`}>
          {/* {isLargeScreen && (
            <div className="py-4 px-6 flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">{headerTitle}</h1>
            </div>
          )} */}

          {/* Priority 1: App Install Banner (shown until app is installed) */}
          {!isInstalled && showInstallBanner && (
            <AppInstallBanner
              deferredPrompt={deferredPrompt}
              onNativeInstall={installApp}
              onDismiss={dismissInstallBanner}
            />
          )}

          {/* Priority 2: Enable Notifications Banner (only shown after app is installed) */}
          {isInstalled && !notificationsEnabled && showNotifBanner && !isOnNotificationsPage && (
            <div className="flex items-center justify-between gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <Bell className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800 truncate">
                  <span className="font-medium">Stay updated!</span> Enable notifications to get instant order alerts.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate('/notifications')}
                  className="text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Enable
                </button>
                <button
                  onClick={dismissNotifBanner}
                  className="text-amber-500 hover:text-amber-700 transition-colors p-0.5"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <Outlet />
        </main>
      </div>

      {/* Bottom Navigation for Mobile */}
      {!isLargeScreen && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-1px_3px_rgba(16,24,40,0.05)] z-30">
          <div className="flex justify-around items-center h-16">
            {bottomNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem === item.name;

              return (
                <button
                  key={item.name}
                  onClick={() => handleMenuClick(item.name, item.path)}
                  className="flex flex-col items-center justify-center flex-1 h-full"
                >
                  <span className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${isActive ? item.color : 'text-gray-400'}`}>
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className={`text-xs mt-0.5 ${isActive ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>{item.name}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* Overlay for mobile when sidebar is open */}
      {!isLargeScreen && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};

export default AdminHeader;
